/**
 * userStateSync — Transparent two-way sync for per-user UI state to MongoDB.
 *
 * Solves: localStorage wiped on app uninstall/update → user loses their tab
 * position, future-reels progress, timeline cursor, etc.
 *
 * Strategy:
 *  1. On login / app open: hydrate() pulls MongoDB state and merges into localStorage.
 *  2. On any tracked localStorage key change: debounced PUT to MongoDB.
 *  3. On reinstall: native device_id + login → MongoDB returns state → hydrates LS.
 *
 * Only tracks UX-state keys (not auth/subscription which are handled separately).
 */
import API from '../config/apiConfig';

const BACKEND_URL = API;

// Keys we mirror to MongoDB. Add more here if user feedback requires it.
// IMPORTANT: Don't track auth/sensitive keys; those have dedicated flows.
export const TRACKED_STATE_KEYS = [
  // Dashboard
  'last_dashboard_tab',           // which tab user last viewed
  'active_content_tab',

  // Future Reels
  'domains_listened',             // JSON array of listened domain ids
  'last_future_reel_domain',
  'future_reels_progress',        // JSON: { [domain]: { position_sec, completed: bool } }

  // Timeline
  'timeline_current_month',
  'timeline_current_year',
  'timeline_selected_domain',

  // Sage
  'sage_last_thread_id',

  // Insights
  'insights_last_tab',

  // Preferences
  'preferred_language',
  'preferred_voice',
  'dark_mode',
];

let syncTimer = null;
let pendingChanges = {};

// Get current user_id from localStorage (set on login)
function getCurrentUserId() {
  try {
    return localStorage.getItem('user_id') || null;
  } catch {
    return null;
  }
}

/**
 * Hydrate localStorage from server on login / app open.
 * MERGES server state into LS — local changes not yet synced are preserved
 * only if localStorage has newer data (LS wins for local-first feel).
 */
export async function hydrateUserState() {
  const userId = getCurrentUserId();
  if (!userId || !BACKEND_URL) return null;
  try {
    const res = await fetch(`${BACKEND_URL}/api/user-state/${encodeURIComponent(userId)}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const serverState = data.state || {};
    let applied = 0;
    for (const [key, value] of Object.entries(serverState)) {
      if (!TRACKED_STATE_KEYS.includes(key)) continue;
      const existing = localStorage.getItem(key);
      // Only overwrite if LS doesn't have value yet (reinstall case)
      if (existing === null || existing === undefined || existing === '') {
        try {
          const str = typeof value === 'string' ? value : JSON.stringify(value);
          localStorage.setItem(key, str);
          applied += 1;
        } catch (_) {}
      }
    }
    if (applied > 0) {
      console.log(`🔄 [userStateSync] Hydrated ${applied} state keys from MongoDB`);
    }
    return serverState;
  } catch (e) {
    console.warn('[userStateSync] hydrate error:', e);
    return null;
  }
}

/**
 * Push a single key-value change to the pending batch.
 * Debounced flush (1.5s) to avoid spamming the backend.
 */
export function pushUserState(key, value) {
  if (!TRACKED_STATE_KEYS.includes(key)) return;
  const userId = getCurrentUserId();
  if (!userId) return;
  let serialized;
  try {
    serialized = typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    serialized = String(value);
  }
  pendingChanges[key] = serialized;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(flushPending, 1500);
}

async function flushPending() {
  const userId = getCurrentUserId();
  if (!userId || Object.keys(pendingChanges).length === 0) return;
  const snapshot = pendingChanges;
  pendingChanges = {};
  try {
    await fetch(`${BACKEND_URL}/api/user-state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, state: snapshot }),
    });
  } catch (e) {
    // On fail, merge back for retry next time
    pendingChanges = { ...snapshot, ...pendingChanges };
  }
}

/**
 * Install a global localStorage.setItem listener that auto-mirrors tracked keys
 * to MongoDB. Must be called ONCE on app init (after storageGuard).
 */
let installed = false;
export function installUserStateSync() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Listen to our custom event from patched localStorage.setItem
  // We hook via a MutationObserver-less approach: wrap setItem once more.
  const origSetItem = Storage.prototype.setItem;
  Storage.prototype.setItem = function (key, value) {
    origSetItem.call(this, key, value);
    if (this === window.localStorage && TRACKED_STATE_KEYS.includes(key)) {
      pushUserState(key, value);
    }
  };

  // Flush pending on page hide (user navigates away or closes app)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushPending();
      }
    });
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', flushPending);
    window.addEventListener('beforeunload', flushPending);
  }

  console.log('📡 [userStateSync] Installed — tracking', TRACKED_STATE_KEYS.length, 'keys');
}
